import DynamicForm from '@remote-com/mushu/src/components/Form/DynamicForm';
import { SuccessTitle, SuccessDescription } from '@remote-com/mushu/src/components/Success';
import SuccessMark from '@remote-com/mushu/src/components/Success/SuccessMark';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import { useFormEvents } from '@remote-com/mushu/src/hooks/useFormEvents';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ButtonInline } from 'remote-com-shared/components/Button';
import { dataLayerPush } from 'remote-com-shared/components/CookieScripts/helpers';
import Modal from 'remote-com-shared/components/ModalForm';
import {
  getCurrentURL,
  registerLead,
  createPartnerStackSignup,
} from 'remote-com-shared/components/ModalForm/helpers/services';
import {
  FormContainer,
  SubmitButton,
  CaptchaWrapper,
  SubsribeSuccessContainer,
  ModalTitle,
  FormError,
  DynamicFormWrapper,
  SubmitButtonWrapper,
  ModalDescription,
} from 'remote-com-shared/components/ModalForm/styles';
import { getFields } from 'remote-com-shared/domains/hubspot/fields';
import { hasImpactFields } from 'remote-com-shared/domains/impact/helpers';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { useImpactFormValues } from 'remote-com-shared/domains/shared/trackingParameters';
import { getPartnerStackInfo } from 'remote-com-shared/utils/partnerstack';
import styled, { css, ThemeProvider } from 'styled-components';
import { v4 as uuid4 } from 'uuid';

import { tabletBreakpoint } from '@/src/domains/shared/breakpoints';
import { FormWrapper } from '@/src/views/ReimagineRemote/styles';

const SuccessContainer = styled(SubsribeSuccessContainer)`
  margin-top: 40px;

  a {
    font-size: inherit;
  }

  ${SuccessDescription} {
    margin-top: 30px;
  }
`;

const FormComponent = styled(FormWrapper)`
  width: 450px;

  ${tabletBreakpoint(css`
    width: auto;
  `)}
`;

function handleWebinarFields(field) {
  const { name } = field;
  switch (name) {
    case 'what_is_one_thing_you_would_like_answered_during_this_event_':
    case 'would_you_be_willing_to_give_a_short_video_testimonial_during_the_event_about_your_experience_using':
      return { ...field, placeholder: ' ', displayLabel: true };
    case 'where_is_your_company_established_or_headquartered_':
      return { ...field, displayLabel: false };
  }
  return field;
}

export function WebinarForm({
  hubspotFormDefinition,
  modalFormTitle,
  modalFormDescription,
  modalFormSubmitLabel,
  inModal = false,
}) {
  const {
    submitText,
    inlineMessage: successMessage,
    formFieldGroups,
    redirect,
  } = hubspotFormDefinition;

  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [isCaptchaDone, setIsCaptchaDone] = useState(false);
  const [showSuccessState, setShowSuccessState] = useState(false);
  const defaultSuccessMessage = 'Thank you!';

  const { formState } = useFormEvents();
  const [formValues, setFormValues] = useState({});

  const reCaptchaRef = useRef(null);
  const impactFormValues = useImpactFormValues();
  const fields = getFields(formFieldGroups).map((field) => handleWebinarFields(field));

  function handleCaptchaChange(value) {
    setIsCaptchaDone(value);
  }

  function timedRedirect(url) {
    setTimeout(function timeoutFn() {
      window.location = url;
    }, 1500);
  }

  async function handleSubmit(values, { setSubmitting }) {
    setShowLoader(true);

    const updatedFormValues = {
      ...formValues,
      ...values,
    };
    setFormValues(updatedFormValues);

    if (isCaptchaDone) {
      const includePartnerstack =
        Object.prototype.hasOwnProperty.call(values, 'pspartnerkey') &&
        Object.prototype.hasOwnProperty.call(values, 'pspartner_group');
      const includeImpactValues = hasImpactFields(values);

      try {
        if (includePartnerstack) {
          try {
            const publicSlug = uuid4();
            await createPartnerStackSignup({
              name: values.name,
              email: publicSlug,
            });
          } catch {
            // Ignore as failing should not break hubspot
          }
        }

        const payload = {
          ...values,
          ...(Object.prototype.hasOwnProperty.call(values, 'utm')
            ? {
                utm: getCurrentURL(),
              }
            : {}),
          ...(includePartnerstack ? getPartnerStackInfo() : {}),
          ...(includeImpactValues ? impactFormValues : {}),
        };

        await registerLead(
          payload,
          `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotFormDefinition.portalId}/${hubspotFormDefinition.guid}/`
        );

        dataLayerPush({
          event: 'user-submitted-hubspot-form',
          label: window.location.pathname,
          conversion: { send_to: 'AW-594140685/SLovCLa8nOEBEI28p5sC' },
        });

        /**
         * Submit dataLayer form submit
         */
        const formSubmitEvent = {
          event: 'user-submitted-hubspot-form',
          id: hubspotFormDefinition.guid,
          pathname: window.location.pathname,
          email: values.email,
        };
        // Provide auto form tracking defined by hubspot
        if (Object.prototype.hasOwnProperty.call(values, 'form_type')) {
          formSubmitEvent.form_type = values.form_type;
        }
        dataLayerPush(formSubmitEvent);

        setShowLoader(false);
        setShowSuccessState(true);
      } catch (exception) {
        reCaptchaRef.current.reset();
        setIsCaptchaDone(false);
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);

        throw exception;
      }
    }
  }

  return (
    <ThemeProvider theme={{ mode: 'light', ...mushuTheme }}>
      <GlobalStyles />
      <FormContainer>
        {showSuccessState ? (
          <SuccessContainer>
            <SuccessMark />
            {successMessage ? (
              <SuccessDescription dangerouslySetInnerHTML={{ __html: successMessage }} />
            ) : (
              <SuccessTitle>{defaultSuccessMessage}</SuccessTitle>
            )}
            {redirect ? timedRedirect(redirect) : null}
          </SuccessContainer>
        ) : (
          <DynamicFormWrapper>
            {modalFormTitle && <ModalTitle>{modalFormTitle}</ModalTitle>}

            {modalFormDescription && <ModalDescription>{modalFormDescription}</ModalDescription>}

            {showFormError && (
              <FormError>
                Something went wrong. Please try again, or contact{' '}
                <ButtonInline href="mailto:help@remote.com">help@remote.com</ButtonInline> if this
                error persists. Thank you.
              </FormError>
            )}

            <DynamicForm
              formName={`${hubspotFormDefinition.guid}${inModal ? '-modal' : ''}`}
              fields={fields}
              fieldValues={formValues}
              formError={formState.errorMessage}
              formTheme="dark"
              formSize="small"
              handleOnSubmit={handleSubmit}
              isLoading={formState.isLoading}
              enableReinitialize
            />

            <CaptchaWrapper>
              <LocaleContextConsumer>
                {(locale) => (
                  <ReCAPTCHA
                    hl={locale}
                    ref={reCaptchaRef}
                    sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                    onChange={handleCaptchaChange}
                  />
                )}
              </LocaleContextConsumer>
            </CaptchaWrapper>

            <SubmitButtonWrapper>
              <SubmitButton
                isLoading={showLoader}
                disabled={!isCaptchaDone}
                form={`${hubspotFormDefinition.guid}${inModal ? '-modal' : ''}`}
              >
                {modalFormSubmitLabel || submitText}
              </SubmitButton>
            </SubmitButtonWrapper>
          </DynamicFormWrapper>
        )}
      </FormContainer>
    </ThemeProvider>
  );
}

export const WebinarFormWrapped = (props) => (
  <FormComponent>
    <WebinarForm {...props} />
  </FormComponent>
);

export const WebinarFormModal = ({ visible, onCancel, ...props }) => (
  <Modal visible={visible} width="660px" footer={null} onCancel={onCancel}>
    <WebinarForm {...props} inModal />
  </Modal>
);
